/**
 * El informe, en pantalla.
 *
 * Una sola vista para los tres sitios donde aparece un diagnóstico: la
 * demostración del motor, el informe propio y el enlace compartido. Tener tres
 * copias del mismo render acabaría con tres informes distintos del mismo dato,
 * y el informe es el producto.
 *
 * No decide nada: recibe el diagnóstico ya calculado y lo presenta. Todo lo que
 * se ve aquí lo produjo el motor en el servidor.
 */

import { Badge, Meter } from "../Meter";
import type { Diagnosis, Report } from "../../lib/api";
import { BRAND, bandColor, dimensionColor, ipcBandColor } from "../../lib/theme";

/** Las bandas viajan en inglés desde el motor; la interfaz va en español. */
const BAND_LABEL: Record<string, string> = { green: "Verde", amber: "Ámbar", red: "Rojo" };

/** La prioridad diagnóstica es una clave del método: se muestra legible. */
const PRIORITY_LABEL: Record<string, string> = {
  viabilidad: "Viabilidad",
  disonancia_dominante: "Disonancia dominante",
  freno_principal: "Freno principal",
  readiness: "Preparación",
  valor_movilizable: "Valor movilizable",
  dirección: "Dirección",
  activación: "Activación",
};

const IPC_PARTS: { key: keyof Report["ipc"]; label: string; weight: string }[] = [
  { key: "I", label: "Intereses", weight: "0,15" },
  { key: "ID", label: "Identidad", weight: "0,15" },
  { key: "F", label: "Frenos (invertidos)", weight: "0,20" },
  { key: "V", label: "Viabilidad", weight: "0,20" },
  { key: "P", label: "Preparación", weight: "0,30" },
];

const DECISION_COLOR: Record<string, string> = {
  A: BRAND.teal,
  B: BRAND.gold,
  C: BRAND.gold,
  D: BRAND.terracotta,
};

function Section({ title, children, wide }: { title: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <section className={`critero-card ${wide ? "lg:col-span-2" : ""}`}>
      <h3 className="mb-4 text-ink">{title}</h3>
      {children}
    </section>
  );
}


export function ReportView({
  data,
  ajeno = false,
}: {
  data: Diagnosis;
  /** El informe se está enseñando a un tercero por enlace compartido. */
  ajeno?: boolean;
}) {
  const r = data.report;
  const ipcColor = ipcBandColor(r.ipc.ipc_band);
  const viability = r.constraints_and_viability;
  const decision = data.strategic.decision;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Cabecera: título del perfil e índice maestro */}
      <div className="critero-card lg:col-span-3">
        <span className="critero-label">{r.profile_subtitle}</span>
        <h2 className="mt-1 text-h3 font-medium text-ink">{r.profile_title}</h2>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <div>
            <span className="critero-label">Índice de preparación</span>
            <div className="mt-1 flex flex-wrap items-baseline gap-4">
              <span className="font-display text-h1 leading-none" style={{ color: ipcColor }}>
                {r.ipc.IPC_final}
              </span>
              <Badge text={r.ipc.ipc_band} color={ipcColor} />
              <Badge
                text={`Nivel ${decision.level} · ${decision.label}`}
                color={DECISION_COLOR[decision.level] ?? BRAND.ink}
              />
            </div>
          </div>
          <div className="grid flex-1 gap-3 sm:grid-cols-5" style={{ minWidth: "20rem" }}>
            {IPC_PARTS.map((part) => (
              <Meter
                key={part.key}
                label={part.label}
                value={r.ipc[part.key] as number}
                color={BRAND.teal}
                hint={`peso ${part.weight}`}
              />
            ))}
          </div>
        </div>

        {r.ipc.guardrails_applied.length > 0 && (
          <div className="mt-6 border-t border-line/40 pt-4">
            <span className="critero-label">Topes aplicados</span>
            <ul className="mt-2 space-y-1">
              {r.ipc.guardrails_applied.map((g) => (
                <li key={g} className="text-caption" style={{ color: BRAND.terracotta }}>
                  {g}
                </li>
              ))}
            </ul>
            <p className="mt-2 max-w-prose text-caption text-ink/60">
              Un tope recorta el índice aunque el resto del perfil puntúe alto: aquí manda la condición
              limitante, no la media.
            </p>
          </div>
        )}
      </div>

      {/* La pregunta que se lleva la persona */}
      <div className="critero-card lg:col-span-3" style={{ backgroundColor: BRAND.paper2 }}>
        <span className="critero-label">{ajeno ? "La pregunta" : "Tu pregunta"}</span>
        <p className="mt-2 max-w-prose font-display text-h3 text-ink">
          {data.decision.main_professional_question}
        </p>
        <p className="mt-3 max-w-prose text-caption text-ink/70">
          <span className="font-medium">{data.decision.dominant_tension.label}.</span>{" "}
          {data.decision.dominant_tension.description}
        </p>
      </div>

      <Section title="Foco del diagnóstico">
        <Badge
          text={
            PRIORITY_LABEL[r.integrated_synthesis.diagnostic_priority.priority] ??
            r.integrated_synthesis.diagnostic_priority.priority
          }
          color={BRAND.teal}
        />
        <p className="mt-3 text-caption text-ink/70">{r.integrated_synthesis.diagnostic_priority.reason}</p>
        <div className="mt-4 border-t border-line/40 pt-4">
          <span className="critero-label">Patrón de decisión</span>
          <p className="mt-1 text-caption font-medium">{data.decision.decision_pattern.label}</p>
          <p className="mt-1 text-caption text-ink/60">{data.decision.decision_pattern.recommendation}</p>
        </div>
        <div className="mt-4 border-t border-line/40 pt-4">
          <span className="critero-label">Pulso</span>
          <p className="mt-1 text-caption">{r.transition_readiness.pulse.overall}</p>
        </div>
      </Section>

      <Section title="Identidad e intereses">
        <dl className="space-y-2 text-caption">
          <div className="flex justify-between gap-4">
            <dt className="text-ink/60">Arquetipo dominante</dt>
            <dd className="text-right font-medium">{r.identity_professional.dominant?.label ?? "—"}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-ink/60">Secundario</dt>
            <dd className="text-right font-medium">{r.identity_professional.secondary?.label ?? "—"}</dd>
          </div>
          {r.identity_professional.isDual && (
            <div className="flex justify-between gap-4">
              <dt className="text-ink/60">Perfil dual</dt>
              <dd className="text-right font-medium">{r.identity_professional.comboLabel}</dd>
            </div>
          )}
          <div className="flex justify-between gap-4">
            <dt className="text-ink/60">Código Holland</dt>
            <dd className="text-right font-medium tracking-widest">{r.interests_fit.code}</dd>
          </div>
        </dl>
        <div className="mt-4 space-y-2">
          {r.interests_fit.top_dims.slice(0, 3).map((d) => (
            <Meter key={d.key} label={d.label} value={d.score} color={BRAND.gold} />
          ))}
        </div>
      </Section>

      <Section title="Marco y valor">
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="critero-label">Viabilidad</span>
              <Badge
                text={BAND_LABEL[viability.feasibility_band] ?? viability.feasibility_band}
                color={bandColor(viability.feasibility_band)}
              />
            </div>
            <Meter
              label="Marco de decisión"
              value={viability.feasibility_score}
              color={bandColor(viability.feasibility_band)}
            />
          </div>
          <Meter
            label={`Valor movilizable · ${r.value_mobilization.band}`}
            value={r.value_mobilization.ivm_global}
            color={BRAND.teal}
          />
          <Meter
            label={`Preparación · ${r.transition_readiness.readiness_label}`}
            value={r.transition_readiness.index}
            color={BRAND.teal}
          />
          {/* critero-lint-disable-next-line potencial · IPL es el nombre de un índice del método */}
          <Meter label={`Potencial latente · ${r.ipl.band}`} value={r.ipl.ipl} color={BRAND.gold} hint={r.ipl.main_block} />
        </div>
        {viability.feasibility_warnings.length > 0 && (
          <ul className="mt-4 space-y-2 border-t border-line/40 pt-4">
            {viability.feasibility_warnings.slice(0, 3).map((w) => (
              <li key={w} className="text-caption text-ink/70">
                {w}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Rueda de transición">
        <div className="space-y-3">
          {/* El color identifica el eje, no lo bien que se haya puntuado. El
              manual lo fija —«el color pertenece a la dimensión, no a la
              puntuación»— y el cambio se nota: antes estas seis barras salían
              rojas, ámbar o verdes, y un informe de carrera se leía como un
              boletín de notas. Lo mal que va algo ya lo dice la cifra, y la
              zona lo dice con palabras. */}
          {r.transition_readiness.dimensions.map((d, i) => (
            <Meter
              key={d.key}
              label={d.label}
              value={d.score}
              color={dimensionColor(i)}
              hint={d.zone}
            />
          ))}
        </div>
        {r.transition_readiness.main_limiter && (
          <p className="mt-4 border-t border-line/40 pt-4 text-caption text-ink/70">
            <span className="font-medium">Eje limitante:</span> {r.transition_readiness.main_limiter.label}.{" "}
            {r.transition_readiness.next_step}
          </p>
        )}
      </Section>

      <Section title="Contradicciones detectadas">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-h2" style={{ color: BRAND.ink }}>
            {r.dissonances_analysis.idd_global}
          </span>
          <Badge text={r.dissonances_analysis.idd_band} color={BRAND.teal} />
          <span className="text-caption text-ink/60">
            {r.dissonances_analysis.active_count} de 10 activas
          </span>
        </div>
        {r.dissonances_analysis.top_active.length === 0 ? (
          <p className="mt-4 text-caption text-ink/60">
            Ninguna de las diez reglas se activa con intensidad relevante.
          </p>
        ) : (
          <ul className="mt-4 space-y-3">
            {r.dissonances_analysis.top_active.map((item) => (
              <li key={item.code} className="border-l-2 pl-3" style={{ borderColor: BRAND.gold }}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-caption font-medium">
                    {item.code} · {item.label}
                  </span>
                  <span className="text-caption tabular-nums text-ink/60">
                    {Math.round(item.severity_adjusted)}
                  </span>
                </div>
                <p className="mt-1 text-caption text-ink/60">{item.evidence.join(" · ")}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Escenarios valorados" wide>
        <div className="grid gap-4 sm:grid-cols-2">
          {r.scenarios.map((s) => (
            <div key={s.id} className="rounded-panel border border-line/40 p-4">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-caption font-medium">{s.label}</span>
                <span className="font-display text-h3" style={{ color: BRAND.ink }}>
                  {s.fit_probability}
                </span>
              </div>
              <p className="mt-2 text-caption text-ink/60">{s.opportunity}</p>
              <p className="mt-2 text-caption" style={{ color: BRAND.terracotta }}>
                {s.risk}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Qué descartar ahora">
        {data.strategic.discard.length === 0 ? (
          <p className="text-caption text-ink/60">
            No hay opciones que convenga descartar por completo en este momento.
          </p>
        ) : (
          <ul className="space-y-3">
            {data.strategic.discard.map((d) => (
              <li key={d.code}>
                <span className="text-caption font-medium">{d.label}</span>
                <p className="mt-1 text-caption text-ink/60">{d.reason}</p>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Qué hacer" wide>
        <ol className="space-y-4">
          {r.recommended_actions.priorities.map((p) => (
            <li key={p.rank} className="flex gap-3">
              <span
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-caption font-medium text-paper"
                style={{ backgroundColor: BRAND.ink }}
              >
                {p.rank}
              </span>
              <div>
                <span className="text-caption font-medium">{p.label}</span>
                <p className="mt-1 text-caption text-ink/60">{p.why}</p>
                <p className="mt-1 text-caption">{p.what}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 rounded-panel p-4" style={{ backgroundColor: BRAND.paper2 }}>
          <span className="critero-label" style={{ color: BRAND.gold }}>
            Esta semana
          </span>
          <p className="mt-1 text-caption">{r.recommended_actions.next_step}</p>
        </div>
      </Section>

      <Section title="Plan 7 / 30 / 90">
        {(
          [
            { label: "7 días", items: r.plan_7_30_90.plan_7 },
            { label: "30 días", items: r.plan_7_30_90.plan_30 },
            { label: "90 días", items: r.plan_7_30_90.plan_90 },
          ] satisfies { label: string; items: string[] }[]
        ).map((horizon) => (
          <div key={horizon.label} className="mb-4 last:mb-0">
            <span className="critero-label">{horizon.label}</span>
            <ul className="mt-2 space-y-2">
              {horizon.items.map((item) => (
                <li
                  key={item}
                  className="border-l-2 pl-3 text-caption text-ink/70"
                  style={{ borderColor: BRAND.line }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Section>

      {r.palancas_profesionales && (
        <Section title="Palancas profesionales">
          <div className="grid grid-cols-3 gap-4 text-center">
            {(
              [
                { label: "Activación", value: r.palancas_profesionales.global_activation_index },
                { label: "Aspiración", value: r.palancas_profesionales.aspiration_index },
                { label: "Brecha", value: r.palancas_profesionales.global_gap_index },
              ] satisfies { label: string; value: number }[]
            ).map((item) => (
              <div key={item.label}>
                <div className="font-display text-h3" style={{ color: BRAND.ink }}>
                  {item.value}
                </div>
                <div className="text-caption text-ink/60">{item.label}</div>
              </div>
            ))}
          </div>
          {r.palancas_profesionales.patterns.length > 0 && (
            <div className="mt-4 border-t border-line/40 pt-4">
              {r.palancas_profesionales.patterns.map((p) => (
                <div key={p.key} className="mb-3 last:mb-0">
                  <span className="text-caption font-medium">{p.label}</span>
                  <p className="mt-1 text-caption text-ink/60">{p.description}</p>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
